import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Shield, Bell, ChevronDown } from "lucide-react";
import { ClinqLogo } from "./ClinqLogo";

const NAV = [
  { to: "/admin/dashboard", label: "Overview" },
  { to: "/admin/analytics", label: "Analytics" },
  {
    label: "Users",
    items: [
      { to: "/admin/users", label: "All Users" },
      { to: "/admin/businesses", label: "Businesses" },
      { to: "/admin/quiz-leads", label: "Quiz Leads" },
      { to: "/admin/rms", label: "Relationship Managers" },
      { to: "/admin/accelerators", label: "Accelerators" },
      { to: "/admin/verified-readiness", label: "Verified Queue" },
    ],
  },
  {
    label: "Support",
    items: [
      { to: "/admin/support-tickets", label: "Support Tickets" },
      { to: "/admin/client-errors", label: "Client Errors" },
      { to: "/admin/expert-sessions", label: "Expert Sessions" },
      { to: "/admin/discovery-sessions", label: "Discovery Sessions" },
      { to: "/admin/coupons", label: "Report Coupons" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/masterclasses", label: "Masterclasses" },
      { to: "/admin/automated-emails", label: "Automated Emails" },
    ],
  },
  {
    label: "Reports",
    items: [
      { to: "/admin/monthly-report", label: "Monthly Report" },
    ],
  },
  { to: "/admin/funders", label: "Funder Directory" },
  { to: "/admin/funder-applications", label: "Applications" },
  { to: "/admin/tenants", label: "Tenants" },
  { to: "/admin/partners", label: "FSD Partners" },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

function isActive(to, current) {
  return (current && current === to) || window.location.pathname.startsWith(to);
}

function NavDropdown({ group, current, openMenu, setOpenMenu, navigate }) {
  const groupActive = group.items.some((i) => isActive(i.to, current));
  const open = openMenu === group.label;
  return (
    <div className="relative" data-nav-dropdown>
      <button
        onClick={() => setOpenMenu(open ? null : group.label)}
        data-testid={`admin-shell-nav-${slug(group.label)}`}
        aria-expanded={open}
        className={"inline-flex items-center gap-1 text-sm font-medium " +
          (groupActive ? "text-[#F59E0B]" : "text-neutral-400 hover:text-neutral-100")}>
        {group.label}
        <ChevronDown className={"h-3.5 w-3.5 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div
          data-testid={`admin-shell-menu-${slug(group.label)}`}
          className="absolute left-0 top-full z-40 mt-2 w-48 overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900 py-1 shadow-xl shadow-black/50">
          {group.items.map((item) => (
            <button key={item.to}
              onClick={() => { setOpenMenu(null); navigate(item.to); }}
              data-testid={`admin-shell-nav-${slug(item.label)}`}
              className={"block w-full px-4 py-2 text-left text-sm font-medium " +
                (isActive(item.to, current)
                  ? "bg-[#F59E0B]/10 text-[#F59E0B]"
                  : "text-neutral-300 hover:bg-neutral-800 hover:text-white")}>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminShell({ children, current }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [openMenu, setOpenMenu] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    if (!openMenu) return;
    const onDocClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openMenu]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    let mounted = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/admin/alerts/unread-count");
        if (mounted) setUnread(data.count || 0);
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[AdminShell] unread-count fetch failed", err);
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, [user?.role]);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" style={{ fontFamily: "Manrope, sans-serif" }}>
      <header className="sticky top-0 z-30 border-b border-neutral-800 bg-neutral-950/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Link to="/admin/dashboard" className="flex items-center gap-2" data-testid="admin-shell-logo">
            <ClinqLogo size="md" tone="amber" testid="admin-shell-logo-mark" />
            <span className="rounded-md bg-[#F59E0B]/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#F59E0B]">
              <Shield className="inline h-3 w-3 mr-1"/>Admin
            </span>
          </Link>
          <div ref={navRef} className="hidden items-center gap-6 md:flex">
            {NAV.map((n) => n.items ? (
              <NavDropdown key={n.label} group={n} current={current}
                openMenu={openMenu} setOpenMenu={setOpenMenu} navigate={navigate} />
            ) : (
              <button key={n.to} onClick={() => { setOpenMenu(null); navigate(n.to); }}
                data-testid={`admin-shell-nav-${slug(n.label)}`}
                className={"text-sm font-medium " +
                  (isActive(n.to, current)
                    ? "text-[#F59E0B]" : "text-neutral-400 hover:text-neutral-100")}>
                {n.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-alerts-bell" onClick={() => navigate("/admin/alerts")}
              className="relative rounded-lg border border-neutral-800 bg-neutral-900 p-2 text-neutral-300 hover:bg-neutral-800">
              <Bell className="h-4 w-4"/>
              {unread > 0 && (
                <span data-testid="admin-alerts-unread-badge"
                  className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            <span className="hidden text-xs text-neutral-500 sm:inline" data-testid="admin-shell-user">{user?.email}</span>
            <button onClick={logout} data-testid="admin-shell-logout"
              className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-neutral-300 hover:bg-neutral-800">
              Log out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-6 py-8">{children}</main>
    </div>
  );
}
